import React from "react";
import { Link } from "react-router-dom";
import { BookOpen, Target, Award, Users, Globe, Heart, ArrowRight, CheckCircle } from "lucide-react";
import { faqData, academicsData } from "../data/mock";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import SEO from "../components/SEO";

const Academics = () => {
  return (
    <main>
      <SEO
        title="Academics | CBSE Curriculum"
        description="TIPS Bengaluru offers CBSE curriculum from Nursery to Grade XII. Enquiry-based, student-centred learning with smart classrooms and holistic education across all 6 campuses."
        keywords="TIPS Bengaluru academics, CBSE curriculum Bangalore, CBSE school Bangalore, CBSE syllabus school Bengaluru, smart classrooms Bangalore school, CBSE grade structure"
        canonical="/academics"
      />
      {/* Page Header */}
      <section className="bg-[#1A0505] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-gray-400 mb-4">
            <Link to="/" className="hover:text-[#C83C4A] transition-colors">Home</Link>
            <span>/</span>
            <span className="text-white">Academics</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>Academics</h1>
          <p className="text-gray-400 mt-3 max-w-2xl">
            Our distinctive academic programmes encourage students to develop critical-thinking through inquiry, reason and reflection.
          </p>
        </div>
      </section>

      {/* Curriculum Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#B41C2C] text-sm font-bold uppercase tracking-widest">Curriculum</span>
            <h2 className="mt-2 text-3xl font-bold text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>Our Academic Programme</h2>
            <p className="mt-3 text-gray-500 max-w-xl mx-auto">All 6 TIPS Bengaluru campuses follow the CBSE curriculum — delivering a consistent, high-quality education from Nursery to Grade XII.</p>
          </div>

          <div className="bg-orange-50 border border-orange-100 rounded-2xl p-8">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-1/2">
                <span className="text-xs font-bold bg-orange-100 text-orange-700 px-3 py-1 rounded-full">CBSE Affiliated · No. 830884</span>
                <h3 className="text-2xl font-bold text-gray-900 mt-4 mb-3" style={{ fontFamily: "'Poppins', sans-serif" }}>Central Board of Secondary Education</h3>
                <p className="text-gray-600 leading-relaxed mb-4">A strong academic framework rooted in national excellence. CBSE at TIPS Bengaluru delivers conceptual clarity and holistic development from Nursery to Grade XII.</p>
                <p className="text-gray-600 leading-relaxed">Teaching and learning at TIPS is student-centered, relevant, and incorporates the multiple intelligences concept in inquiry with differentiation at various levels.</p>
              </div>
              <div className="lg:w-1/2">
                <h4 className="font-bold text-gray-800 mb-4">Grade Structure</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {academicsData.map((stage) => (
                    <div key={stage.id} className="bg-white rounded-xl p-4 border border-orange-100">
                      <div className="font-bold text-gray-900 text-sm mb-1">{stage.title}</div>
                      <div className="text-[#B41C2C] text-xs font-semibold mb-2">{stage.grades}</div>
                      <div className="flex flex-wrap gap-1">
                        {stage.features.slice(0, 2).map((f) => (
                          <span key={f} className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded">{f}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#B41C2C] text-sm font-bold uppercase tracking-widest">Teaching Philosophy</span>
            <h2 className="mt-2 text-3xl font-bold text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>The TIPS Approach to Learning</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: BookOpen, title: "Student-Centred Learning", desc: "Teaching at TIPS is student-centered, relevant, and incorporates the multiple intelligences concept in inquiry with differentiation at various levels." },
              { icon: Target, title: "Critical Thinking", desc: "Our distinctive academic programmes encourage students to develop critical-thinking through inquiry, reason and reflection." },
              { icon: Award, title: "National Benchmarks", desc: "Achieving top scores at the national level consistently. Our internal assessment and feedback makes board exams relatively easier." },
              { icon: Users, title: "Qualified Educators", desc: "Educators drawn from various aspects and experiences of life provide a holistic perspective to education with personalized attention." },
              { icon: Globe, title: "Global Opportunities", desc: "Regular Student Exchange Programmes and opportunities to participate in Global MUNs and Globeducate Programmes internationally." },
              { icon: Heart, title: "Holistic Development", desc: "Beyond academics, students learn skills, discover talents, forge personalities, build character and make friends for life." }
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-[#FDF5F6] rounded-xl flex items-center justify-center mb-4">
                  <Icon size={22} className="text-[#B41C2C]" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">{title}</h4>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Grade 11 Apply */}
      <section className="py-16 bg-[#1A0505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-10 items-center">
            <div className="lg:w-1/2">
              <span className="text-[#C83C4A] text-sm font-bold uppercase tracking-widest">Grade 11 Admissions</span>
              <h2 className="text-3xl font-bold text-white mt-2 mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>Apply Now for Grade 11 Admissions</h2>
              <p className="text-gray-400 mb-6">Career-focused high school years with Science, Commerce, and Humanities streams.</p>
              <ul className="space-y-3 mb-7">
                {[
                  "Academic Excellence – National level top scores every year",
                  "Qualified educators with personalized attention",
                  "Rigorous internal assessment & feedback",
                  "Career fairs twice a year with top institutes",
                  "Student Exchange Programmes across International Campuses",
                  "Opportunities to participate in Global MUNs"
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-gray-300">
                    <CheckCircle size={15} className="text-[#C83C4A] mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/admissions" className="inline-flex items-center gap-2 bg-[#B41C2C] hover:bg-[#9B1522] text-white font-semibold px-7 py-3 rounded-xl transition-colors">
                Apply Online <ArrowRight size={16} />
              </Link>
            </div>
            <div className="lg:w-1/2">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
                <h3 className="text-white font-bold text-xl mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>Frequently Asked Questions</h3>
                <Accordion type="single" collapsible className="space-y-2">
                  {faqData.map((faq) => (
                    <AccordionItem key={faq.id} value={`faq-${faq.id}`} className="border border-white/10 rounded-xl px-4">
                      <AccordionTrigger className="text-white text-sm font-medium text-left py-3 hover:no-underline">{faq.question}</AccordionTrigger>
                      <AccordionContent className="text-gray-400 text-sm pb-4 leading-relaxed">{faq.answer}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Academics;
